import { useState } from "react";
import { Form, Formik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useCustomerService from "../../services/api/customerService";
import useCampaignService from "../../services/api/campaignService";
import { customerCreateValidationSchema } from "../../schemas/customer";
import { enums } from "../../constants/enum";

import { AddIcon } from "../../assets/icons";
import { Button, Loading } from "../../components";
import {
  DateInput,
  Radio,
  SelectInput,
  TextInput,
} from "../../components/forms";
import cities from "../../assets/data/cities.json";

function NewCustomerTab({ onSelect }) {
  const initialValues = {
    title: enums.CUSTOMER_TITLES.MR,
    name: "",
    dob: "",
    mobileNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    type: enums.CUSTOMER_TYPES.NORMAL,
    campaignId: "",
  };
  const [isSaving, setIsSaving] = useState(false);
  const { createCustomer } = useCustomerService();
  const mutation = useMutation({
    mutationFn: createCustomer,
  });
  const { getCampaignsWithoutPagination, queryKey } = useCampaignService();
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: [queryKey, { page: "*", limit: "*" }],
    queryFn: getCampaignsWithoutPagination,
  });
  const handleSave = (values) => {
    mutation.mutate(
      { payload: { ...values, mobileNumber: `0${values.mobileNumber}` } },
      {
        onSuccess: async (payload) => {
          toast.success(payload?.message);
          onSelect((prev) => ({
            ...prev,
            customer: payload?.customerId || null,
          }));
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.payload?.message ||
              enums.DEFAULT_ERROR_MESSAGE,
          );
        },
        onSettled: () => {
          setIsSaving(false);
        },
      },
    );
  };

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }
  return (
    <div className="customer-form">
      <Loading isLoading={isSaving} variant="overlay" />
      <Formik
        initialValues={initialValues}
        validationSchema={customerCreateValidationSchema}
        onSubmit={handleSave}
      >
        {({ values }) => (
          <Form>
            <div className="input-group">
              <div className="radio-group">
                <Radio
                  name="title"
                  value={enums.CUSTOMER_TITLES.MR}
                  label="Mr."
                />
                <Radio
                  name="title"
                  value={enums.CUSTOMER_TITLES.MRS}
                  label="Mrs."
                />
                <Radio
                  name="title"
                  value={enums.CUSTOMER_TITLES.REVEREND}
                  label="Reverent"
                />
              </div>

              <TextInput label="Name" name="name" placeholder="Type here" />
            </div>

            <div className="input-group">
              <DateInput label="Birthday" name="dob" />
              <TextInput
                label="Phone"
                name="mobileNumber"
                placeholder="Type here"
                prefix="+94"
              />
            </div>

            <TextInput
              label="Address 1"
              name="addressLine1"
              placeholder="Type here"
            />
            <TextInput
              label="Address 2"
              name="addressLine2"
              placeholder="Type here"
            />

            <TextInput
              label="City"
              name="city"
              isDataListed
              dataListItems={cities}
              placeholder="Type here"
            />

            <SelectInput
              label="Customer type"
              name="type"
              values={[
                {
                  label: enums.CUSTOMER_TYPES.NORMAL,
                  value: enums.CUSTOMER_TYPES.NORMAL,
                },
                {
                  label: enums.CUSTOMER_TYPES.EYE_CAMP,
                  value: enums.CUSTOMER_TYPES.EYE_CAMP,
                },
              ]}
            />
            {values.type === enums.CUSTOMER_TYPES.EYE_CAMP ? (
              <SelectInput
                label="Campaign Details"
                name="campaignId"
                values={[
                  { label: "Select Campaign", value: "", disabled: true },
                  ...campaigns.map((c) => ({
                    value: c._id,
                    label: `${c.orderNumberSequencePrefix} - ${c.name} - ${c.place}`,
                  })),
                ]}
              />
            ) : null}

            <Button
              type="submit"
              disabled={isSaving}
              text="Add New Customer"
              icon={<AddIcon />}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default NewCustomerTab;
